import {
  sha256,
  bytesToHex,
  hexToBytes,
  textEncoder,
  textDecoder,
} from './utils.js'
import { sign, verify } from './crypto.js'
import { ENCODING_FORMATS } from './constants.js'

/**
 * @typedef {import('./types.js').WorkerRequest} WorkerRequest
 */

/**
 * Packs a payload into a signed WorkerRequest
 *
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Sender's Ed25519 public key
 * @param {Uint8Array} params.privKey - Sender's Ed25519 private key
 * @param {Object} params.payload - Payload object to pack
 * @returns {WorkerRequest} Signed worker request
 */
export const packWorkerRequest = ({ publicKey, privKey, payload }) => {
  // Serialize payload to JSON bytes
  const payloadBytes = textEncoder.encode(JSON.stringify(payload))

  // Hash the payload (we sign the hash, not the full payload)
  const hash = sha256(payloadBytes)

  // Sign the hash with Ed25519
  const signature = sign(privKey, hash)

  return {
    payload: bytesToHex(payloadBytes),
    public_key: bytesToHex(publicKey),
    signature: bytesToHex(signature),
    encoding: ENCODING_FORMATS.HEX,
  }
}

/**
 * Unpacks and verifies a WorkerRequest, returning the payload
 *
 * @param {WorkerRequest} request - Worker request to unpack
 * @returns {Object} Parsed payload object
 * @throws {Error} If signature verification fails
 */
export const unpackWorkerRequestPayload = (request) => {
  // Verify signature first (throws if invalid)
  verifyWorkerRequestSignature(request)

  // Decode payload from hex to bytes
  const payloadBytes = hexToBytes(request.payload)

  // Parse JSON and return
  return JSON.parse(textDecoder.decode(payloadBytes))
}

/**
 * Verifies the Ed25519 signature of a WorkerRequest
 *
 * @param {WorkerRequest} request - Worker request to verify
 * @throws {Error} If signature is invalid
 */
export const verifyWorkerRequestSignature = ({ public_key, payload, signature }) => {
  // Compute hash of the payload (same as during signing)
  const hash = sha256(hexToBytes(payload))

  // Verify Ed25519 signature
  const isValid = verify(hexToBytes(public_key), hash, hexToBytes(signature))

  if (!isValid) {
    throw new Error('signature is not valid')
  }
}

/**
 * Validates that a WorkerRequest has all required fields
 *
 * @param {WorkerRequest} request - Worker request to validate
 * @throws {Error} If required fields are missing
 */
export const validateWorkerRequest = (request) => {
  if (!request.public_key) {
    throw new Error('public_key is required')
  }

  if (!request.signature) {
    throw new Error('signature is required')
  }

  if (!request.payload) {
    throw new Error('payload is required')
  }
}

/**
 * Fully validates a WorkerRequest (fields + signature)
 *
 * @param {WorkerRequest} request - Worker request to validate
 * @throws {Error} If validation or verification fails
 */
export const validateAndVerifyWorkerRequest = (request) => {
  validateWorkerRequest(request)
  verifyWorkerRequestSignature(request)
}

// Legacy export (deprecated, use validateWorkerRequest)
export const checkWorkerRequest = validateWorkerRequest
